import { useEffect, useRef } from 'react';

interface UseRAFScrollOptions {
  /**
   * Callback to execute on scroll (debounced via requestAnimationFrame)
   */
  onScroll: () => void;
  /**
   * Whether the scroll listener is enabled (default: true)
   */
  enabled?: boolean;
  /**
   * Whether to also listen to resize events (default: false)
   */
  listenToResize?: boolean;
}

/**
 * A hook that provides RAF-debounced scroll (and optionally resize) event handling.
 * Ensures only one animation frame is scheduled at a time for smooth performance.
 */
export function useRAFScroll({ onScroll, enabled = true, listenToResize = false }: UseRAFScrollOptions): void {
  const rafIdRef = useRef<number | null>(null);
  const isScheduledRef = useRef(false);
  const onScrollRef = useRef(onScroll);

  // Keep callback ref in sync to avoid stale closures
  useEffect(() => {
    onScrollRef.current = onScroll;
  }, [onScroll]);

  useEffect(() => {
    if (!enabled) return;

    const executeCallback = () => {
      onScrollRef.current();
      isScheduledRef.current = false;
    };

    const handleEvent = () => {
      if (isScheduledRef.current) return;
      isScheduledRef.current = true;
      rafIdRef.current = window.requestAnimationFrame(executeCallback);
    };

    // Initial call
    handleEvent();

    window.addEventListener('scroll', handleEvent, { passive: true });

    if (listenToResize) {
      window.addEventListener('resize', handleEvent, { passive: true });
    }

    return () => {
      window.removeEventListener('scroll', handleEvent);

      if (listenToResize) {
        window.removeEventListener('resize', handleEvent);
      }

      if (rafIdRef.current !== null && typeof window.cancelAnimationFrame === 'function') {
        window.cancelAnimationFrame(rafIdRef.current);
      }
    };
  }, [enabled, listenToResize]);
}
